import React from "react";
import { Box, Container, Typography, Chip, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import EuroIcon from "@mui/icons-material/Euro";
import NearMeIcon from "@mui/icons-material/NearMe";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useLanguage } from "../../contexts/LanguageContext";
import SEOHead from "../../components/SEOHead";
import {
  PRICE_CATEGORIES,
  PREISE_KEY,
  PREISE_DEFAULTS,
  applyCategoryContent,
} from "../../content/preise";
import { useSiteContent } from "../../hooks/useSiteContent";

export { PRICE_CATEGORIES, PRICE_DATA, SECTION_IMAGES } from "../../content/preise";
export type { PriceItem, PriceSection, PriceCategory } from "../../content/preise";

const MotionBox = motion(Box);

const PreisePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const preiseContent = useSiteContent(PREISE_KEY, PREISE_DEFAULTS);
  const categories = PRICE_CATEGORIES.map((c) => applyCategoryContent(c, preiseContent));

  return (
    <Box>
      <SEOHead
        title={t('seo.preise_title')}
        description={t('seo.preise_description')}
        canonicalPath="/preise"
      />
      {/* ── Header ── */}
      <Box sx={{ bgcolor: "#f8f8f8", borderBottom: "1px solid #e8e8e8" }}>
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 12, md: 14 }, pb: { xs: 6, md: 8 } }}
        >
          <Chip
            icon={<EuroIcon sx={{ fontSize: "14px !important" }} />}
            label={t("preise.lohn_chip")}
            sx={{
              bgcolor: "#eef0fd",
              color: "#3b52d4",
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: "0.1em",
              mb: 3,
              border: "1px solid #d0d5f8",
            }}
          />
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              letterSpacing: "3px",
              mb: 2,
              display: "block",
            }}
          >
            {t("nav.preise")}
          </Typography>
          <Typography
            variant="h1"
            sx={{ mb: 3, fontSize: { xs: "2.5rem", md: "3.5rem" } }}
          >
            {t("preise.title")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 560,
              fontSize: "1.1rem",
              lineHeight: 1.75,
            }}
          >
            {t("preise.subtitle")}
          </Typography>
        </Container>
      </Box>

      {/* ── Category Cards ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {categories.map((cat, index) => (
            <Grid item xs={12} md={6} key={cat.id}>
              <MotionBox
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() =>
                  navigate(`/preise/${cat.id}`, { state: { category: cat } })
                }
                sx={{
                  height: { xs: 420, md: 520 },
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  cursor: "pointer",
                  backgroundImage: `url(${cat.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "transform 0.35s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    "& .card-blur": { opacity: 1 },
                    "& .card-hover": { opacity: 1, transform: "translateY(0)" },
                    "& .card-default": { opacity: 0 },
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.75) 100%)",
                    zIndex: 1,
                  },
                }}
              >
                {/* Default content */}
                <Box
                  className="card-default"
                  sx={{
                    transition: "opacity 0.3s ease",
                    zIndex: 2,
                    position: "absolute",
                    inset: 0,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      position: "absolute",
                      top: 20,
                      left: 20,
                      color: "rgba(255,255,255,0.75)",
                      letterSpacing: "2.5px",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                    }}
                  >
                    {t("preise.lohn_chip")}
                  </Typography>
                  <Typography
                    variant="h4" component="p"
                    sx={{
                      position: "absolute",
                      bottom: 56,
                      left: 24,
                      right: 24,
                      color: "white",
                      fontWeight: 800,
                      fontSize: { xs: "1.6rem", md: "2rem" },
                      lineHeight: 1.2,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {t(cat.titleKey)}
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: 26,
                      left: 24,
                      right: 24,
                      color: "rgba(255,255,255,0.65)",
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {t(cat.subtitleKey)}
                  </Typography>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 24,
                      right: 24,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <NearMeIcon
                      sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}
                    />
                  </Box>
                </Box>

                {/* Blur overlay */}
                <Box
                  className="card-blur"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backdropFilter: "blur(8px)",
                    bgcolor: "rgba(0,0,0,0.55)",
                    zIndex: 3,
                    opacity: 0,
                    transition: "opacity 0.35s ease",
                  }}
                />

                {/* Hover info */}
                <Box
                  className="card-hover"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    px: 4,
                    py: 4,
                    opacity: 0,
                    transform: "translateY(12px)",
                    transition: "opacity 0.35s ease, transform 0.35s ease",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      letterSpacing: "2px",
                      fontSize: "0.65rem",
                      mb: 1.5,
                    }}
                  >
                    {t("preise.lohn_chip")}
                  </Typography>
                  <Typography
                    variant="h3" component="h2"
                    sx={{
                      color: "white",
                      fontWeight: 800,
                      mb: 2,
                      lineHeight: 1.2,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {t(cat.titleKey)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.75)",
                      lineHeight: 1.7,
                      mb: 3,
                      fontSize: "0.9rem",
                      maxWidth: 320,
                    }}
                  >
                    {t(cat.subtitleKey)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      mb: 3,
                      fontSize: "0.85rem",
                    }}
                  >
                    {cat.sections.length} Leistungsbereiche
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      color: "white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.8rem",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                      }}
                    >
                      Preisliste ansehen
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Box>
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PreisePage;
